import React, {ReactEventHandler, useRef, useState} from 'react';
import b_ from 'b_';
import ReactCrop, {
    centerCrop,
    makeAspectCrop,
    PixelCrop,
    PercentCrop,
    convertToPixelCrop
} from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import {Icon, Modal, Button} from 'client/common/components/lego';
import {useIsMobileOrTablet} from 'client/common/hooks/device';
import {usePopup} from 'client/common/utils/popup';

import I18N from 'assets/i18n/brandlift-project';

import canvasPreview from './canvas-preview';
import './image-cropper.css';

const b = b_.with('image-cropper');

const i18n = I18N.common.imageCropper;

interface ImageCropperProps {
    image: string;
    name: string;
    visible: boolean;
    onImageCropped: (file: File) => void;
    defaultAspect?: number;
    handleClose: () => void;
}

// eslint-disable-next-line max-statements
export default function ImageCropper(props: ImageCropperProps) {
    const {image, name, onImageCropped, handleClose, visible, defaultAspect = 16/9} = props;

    const imageRef = useRef(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [widthHeight, setWidthHeight] = useState([0, 0]);
    const [aspect, setAspect] = useState<number>(defaultAspect);
    const [crop, setCrop] = useState<PixelCrop | PercentCrop>();

    const menuIconRef = useRef(null);
    const {popup: PopupMenu, toggle} = usePopup(menuIconRef);

    const onLoad: ReactEventHandler<HTMLImageElement> = e => {
        const {width, height} = e.currentTarget;

        const cropObj = makeAspectCrop(
            {
                unit: '%',
                width: 30
            },
            aspect,
            width,
            height
        );

        const centredCrop = centerCrop(cropObj, width, height) as PercentCrop;

        setCrop(centredCrop);
        setWidthHeight([width, height]);
    };

    const onChangeCrop = (_pixelCrop: PixelCrop, percentCrop: PercentCrop) => {
        setCrop(percentCrop);
    };

    const aspectChange = (n: number) => {
        const cropObj = makeAspectCrop(
            {
                ...crop,
                unit: '%'
            },
            n,
            widthHeight[0],
            widthHeight[1]
        );

        const centredCrop = centerCrop(cropObj, widthHeight[0], widthHeight[1]);

        setCrop(centredCrop);
        setAspect(n);

        toggle();
    };

    const isMobile = useIsMobileOrTablet();

    const handleFileGenerate = async () => {
        if (imageRef.current && canvasRef.current) {
            const {width, height} = imageRef.current;

            canvasPreview(
                imageRef.current,
                canvasRef.current,
                convertToPixelCrop(crop as PercentCrop, width, height)
            );

            canvasRef.current.toBlob(blob => {
                if (blob) {
                    const file = new File([blob], name, {type: blob.type});

                    onImageCropped(file);
                }
            });
        }
    };

    return (
        <Modal onClose={handleClose} className={b('modal')} visible={visible} theme='normal'>
            <div className={b('button-wrapper')}>
                <Button
                    size='icon'
                    view='clear'
                    icon={(iconClassName: string) => (
                        <Icon className={iconClassName} type='cross' />
                    )}
                    className={b('button', {type: 'close'})}
                    onClick={handleClose}
                />
            </div>

            <div className={b('wrapper')}>
                <div className={b('modal')}>
                    <div className={b('')}>
                        <ReactCrop crop={crop} aspect={aspect} onChange={onChangeCrop}>
                            <img ref={imageRef} src={image} onLoad={onLoad} />
                        </ReactCrop>
                    </div>

                    {crop && (
                        <canvas
                            ref={canvasRef}
                            style={{
                                display: 'none',
                                width: 150,
                                height: 150,
                                objectFit: 'contain'
                            }}
                        />
                    )}
                </div>

                <div className={b('footer')}>
                    <div className={b('buttons-wrapper')} ref={menuIconRef}>
                        <Button
                            onClick={toggle}
                            view='dl-default'
                            size='m'
                            className={b('button')}
                            iconRight={
                                isMobile
                                    ? undefined
                                    : (className: string) => (
                                        <Icon
                                            className={className}
                                            type='arrow'
                                        />
                                    )
                            }
                        >
                            {i18n.aspect}
                        </Button>

                        <PopupMenu className={b('popup')} view='dl-light'>
                            <div onClick={() => aspectChange(1)} className={b('popup-option')}>
                                1
                            </div>
                            <div onClick={() => aspectChange(4 / 3)} className={b('popup-option')}>
                                4/3
                            </div>
                            <div onClick={() => aspectChange(3 / 4)} className={b('popup-option')}>
                                3/4
                            </div>
                            <div onClick={() => aspectChange(16 / 9)} className={b('popup-option')}>
                                16/9
                            </div>
                            <div onClick={() => aspectChange(9 / 16)} className={b('popup-option')}>
                                9/16
                            </div>
                        </PopupMenu>
                    </div>

                    <Button
                        view='dl-action'
                        size='m'
                        onClick={handleFileGenerate}
                    >
                        {i18n.upload}
                    </Button>
                </div>
            </div>
        </Modal>
    );
}
